import { RelatedParty } from '../../../../../../../../../types/api';
import {
  getPG1Year,
  getTaxYears,
} from '../../../../../../components/loan_application/utils/helpers';
import {
  LIMITS,
  MAX_NUMBER_INPUT_VALUE,
  NUMBER_FORMATS,
  RENT_OWN,
  APPLICATION_BORROWER_BUSINESS_STRUCTURE,
  SF_MAX_CHAR_LIMITS,
  UNITED_STATES,
  YES_NO,
  BUSINESS_OWNERSHIP_STATUS_OPTIONS,
  BUSINESS_OWNERSHIP_STATUS_EXCLUSIVE_OPTIONS,
  NUMBER_OF_EMPLOYEES_RANGE_OPTIONS,
  MARKET_ESTIMATION_VALUE_CHOICES,
  SYSTEM_PROPERTY_RECORD_TYPE_CHOICES,
  PLACEHOLDERS,
  ETHNICITY_OPTIONS,
  ETHNICITY_OPTIONS_EXCLUSIVE,
  SUB_ETHNICITY_OPTIONS,
  RACE_OPTIONS,
  RACE_OPTIONS_EXCLUSIVE,
  SUB_RACE_OPTIONS,
  OTHER_RACE_TOOLTIPS,
  BEGIN_INSTALLATION_EXPECTED_OPTIONS,
  PRIMARY_MOTIVATION_FOR_SOLAR_OPTIONS,
} from '../../../../../../constants';
import loanFormModelOriginal, {
  taxDocuments as taxDocumentsOriginal,
} from '../../../../../../components/loan_application/form_models/loanFormModel';

const required = true as const;
const [currentYear, lastYear, twoYearsAgo, threeYearsAgo, fourYearsAgo] =
  getTaxYears();

/** NWF: shared by company + HOA required-docs (base defines `name`). */
const nwfMeetingMinutesFormField = {
  ...loanFormModelOriginal.formField.hoaDocuments.meetingMinutes,
  label: 'Meeting Minutes Authorizing the Financing of This Project.',
  tooltip:
    'Any documentation showing proper authorization to proceed with the financing of this project.',
};

const loanFormModel = {
  formField: {
    ...loanFormModelOriginal.formField,
    borrowerEIN: {
      name: 'borrower_tin',
      label: 'Employer Identification Number (EIN)',
      requiredErrorMsg: 'EIN is required',
      getRequired: (borrowerBusinessStructure: string) =>
        borrowerBusinessStructure !== 'SOLE PROPRIETORSHIP',
      format: NUMBER_FORMATS.EIN,
      getTooltip: (borrowerLegalName: string) =>
        `Please provide the Employer Identification Number (EIN) or Tax ID ${borrowerLegalName} uses to file documents with the IRS. You should be able to find this on a previously filed tax return or other IRS document, or your IRS form SS-4. If you are a sole proprietor and have not applied for an EIN, please input your social security number.`,
    },
    businessAddressIsSameAsSystemProperty: {
      name: 'sys_prop_address_is_same_as_business' as const,
      getLabel: (solarSystemLocationAddress: string) =>
        `Business Address is ${solarSystemLocationAddress}`,
      choices: YES_NO,
      required,
    },
    mailingAddressSameAsBusinessPicklist: {
      name: 'mailing_address_same_as_bussiness' as const,
      getLabel: (businessAddress: string) =>
        `Mailing Address is ${businessAddress}`,
      choices: YES_NO,
      required,
    },
    systemPropertyAddress: {
      name: 'sys_prop_address',
      label: 'Street Address',
      placeholder: 'Enter street address',
      required,
      requiredErrorMsg:
        'Address of the property where the project will be completed is a required field',
      infoText:
        'Please enter the physical address of the property where the project will be completed.',
      maxLength: SF_MAX_CHAR_LIMITS.CHAR_FIELD,
    },
    systemPropertyAddressCity: {
      name: 'sys_prop_address_city',
      label: 'City',
      placeholder: 'Enter city',
      required,
      maxLength: SF_MAX_CHAR_LIMITS.CHAR_FIELD,
      requiredErrorMsg:
        'City of the property where the project will be completed is a required field',
    },
    systemPropertyAddressState: {
      name: 'sys_prop_address_state',
      label: 'State',
      choices: UNITED_STATES,
      placeholder: 'Choose state',
      required,
      requiredErrorMsg:
        'State of the property where the project will be completed is a required field',
    },
    systemPropertyAddressZipCode: {
      name: 'sys_prop_address_zip_code',
      label: 'Zip Code',
      placeholder: 'Enter 5 digit zip code',
      format: NUMBER_FORMATS.ZIP,
      required,
      requiredErrorMsg:
        'Zip code of the property where the project will be completed is a required field',
    },
    systemPropertyAppraisedValue: {
      name: 'system_property_appraised_value' as const,
      label: 'Estimated market value of the property above',
      required,
    },
    systemPropertyEstimationMethod: {
      name: 'system_property_estimation_method' as const,
      label: 'How was the estimated market value above determined?',
      requiredErrorMsg: 'Field is required',
      choices: MARKET_ESTIMATION_VALUE_CHOICES,
      required,
    },
    // Related Party: Landlord type
    systemPropertyRecordType: {
      name: 'principal_type' as const,
      label: 'Owner of the property above is...',
      requiredErrorMsg: 'This field is required',
      choices: SYSTEM_PROPERTY_RECORD_TYPE_CHOICES,
      required,
    },
    systemPropertyOwnerFirstName: {
      name: 'first_name' as const,
      label: 'First name of the property owner',
      required,
      requiredErrorMsg: 'This field is required',
      infoText:
        'Enter the legal first name of the individual that owns the property where the project will be completed. We will not reach out to them until we receive your consent.',
    },
    systemPropertyOwnerLastName: {
      name: 'last_name' as const,
      label: 'Last name of the property owner',
      required,
      requiredErrorMsg: 'This field is required',
    },
    systemPropertyOwnerBusinessName: {
      name: 'business_name' as const,
      label: 'Business name of the property owner',
      required,
      requiredErrorMsg: 'This field is required',
      infoText:
        'Enter the legal name of the business that owns the property where the project will be completed. We will not reach out to them until we receive your consent.',
    },
    systemPropertyOwnerEmail: {
      name: 'email' as const,
      getLabel: (ownerName: string) => `Email for ${ownerName}`,
      required,
      requiredErrorMsg: 'This field is required',
    },
    systemPropertyOwnerPhone: {
      name: 'phone_number' as const,
      getLabel: (ownerName: string) => `Phone number for ${ownerName}`,
      required,
      requiredErrorMsg: 'This field is required',
      format: NUMBER_FORMATS.PHONE,
      placeholder: PLACEHOLDERS.PHONE,
    },
    systemPropertyOwnerRelatedToBorrower: {
      name: 'sys_prop_owner_related_to_borrower',
      getLabel: (borrowerLegalName: string, propertyLegalNameOfOwner: string) =>
        `Is ${propertyLegalNameOfOwner} related to ${borrowerLegalName}?`,
      choices: YES_NO,
      getTooltip: (
        borrowerLegalName: string,
        propertyLegalNameOfOwner: string
      ) => `
      ${propertyLegalNameOfOwner} and ${borrowerLegalName} are related if they are owned or controlled by one or more of the same individual(s) or entity(s).`,
      required,
    },
    systemPropertyOwnerPercentOwned: {
      name: 'property_owner_percent_ownership',
      getLabel: (propertyOwner: string) =>
        `How much of the property above does ${propertyOwner} own?`,
      required,
      requiredErrorMsg: 'This field is required',
      max: 100,
    },

    principalIdDocument: {
      name: 'Principal ID' as const,
      label:
        'Government issued ID such as a State driver’s license or passport',
      getLabel: (
        principal_first_name: string,
        principal_last_name: string,
        index: number,
        isForProfit: boolean
      ) => {
        const defaultName = isForProfit
          ? `Principal ${index}`
          : `Authorized Board Member ${index}`;
        const name =
          principal_first_name && principal_last_name
            ? `${principal_first_name} ${principal_last_name}`
            : defaultName;
        return `Upload a government-issued ID for ${name}`;
      },
      required,
      infoText:
        "Provide a copy of a government issued form of identification such as a driver's license or passport for each beneficial owner and controlling party.",
      requiredErrorMsg: 'Copy of Government-Issued ID is required',
    },
    systemPropertyOwnershipStatus: {
      name: 'sys_prop_ownership_status_picklist' as const,
      getLabel: (borrowerLegalName: string) =>
        `Does ${borrowerLegalName} own or lease the property above?`,
      required,
      requiredErrorMsg: 'Ownership Status is required',
      choices: [
        {
          value: 'OWNED',
          label: 'Own',
        },
        {
          value: 'LEASED',
          label: 'Lease',
        },
      ],
    },
    systemPropertyLegalNameOfOwner: {
      name: 'system_property_legal_name_of_owner' as const,
      label: 'Enter the name of the owner of the property.',
      tooltip:
        'Enter the legal name of the individual or entity that owns the property where the project will be completed. We will not reach out to them until we receive your consent.',
      required,
      maxLength: SF_MAX_CHAR_LIMITS.CHAR_FIELD,
      requiredErrorMsg: 'This field is required',
    },
    systemPropertyLandlordConsent: {
      name: 'sys_prop_landlord_consent_confirmation',
      getLabel: (_borrowerLegalName: string) =>
        'Has the property owner agreed to the project you are looking to finance at their property?',
      infoText:
        'Once approved for the loan, we will require your landlord to provide their consent in writing as a condition of loan funding.',
      getTooltip: (
        borrowerLegalName: string,
        propertyLegalNameOfOwner: string
      ) =>
        `If ${borrowerLegalName} is approved for the loan, we will require ${propertyLegalNameOfOwner} to provide their consent in writing as a condition of loan funding.`,
      choices: YES_NO,
      required,
      requiredErrorMsg: 'Landlord consent information is required',
    },
    systemPropertyOwnerAffiliatedWithBorrower: {
      name: 'sys_prop_owner_affiliated_with_borrower',
      getLabel: (borrowerLegalName: string, propertyLegalNameOfOwner: string) =>
        `Is ${propertyLegalNameOfOwner} related to ${borrowerLegalName}?`,
      choices: YES_NO,
      getTooltip: (
        borrowerLegalName: string,
        propertyLegalNameOfOwner: string
      ) =>
        `${borrowerLegalName} and ${propertyLegalNameOfOwner} are related if they are owned or controlled by one or more of the same individual(s) or entity(s).`,
      required,
    },
    systemPropertyLeaseExpirationYear: {
      name: 'system_property_lease_expiration_year' as const,
      label: 'Year of lease expiration',
      format: NUMBER_FORMATS.YEAR,
      tooltip:
        'Lease term should be at least 80% of the loan term, with the exception of major property modifications.',
      lease80percentLoanTermWarning:
        'We ordinarily require the remaining lease length to be 80% of the loan term. A member of our underwriting team will reach out to you upon submission regarding your particular situation.',
      required,
    },
    systemPropertyCurrentlyPayingMortgage: {
      name: 'borrower_is_currently_paying_mortgage' as const,
      label: `Is there a mortgage outstanding for the property above?`,
      required,
      choices: YES_NO,
    },
    borrowerBusinessStructure: {
      name: 'borrower_structure' as const,
      label: 'Business Structure',
      choices: [
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.LLC,
          label: 'LLC',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.LLP,
          label: 'LLP',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.SCORP,
          label: 'S-Corp',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.CCORP,
          label: 'C-Corp',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.GENERAL_PARTNERSHIP,
          label: 'General Partnership',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.SOLE_PROPRIETORSHIP,
          label: 'Sole Proprietorship',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.IRREVOCABLE_TRUST,
          label: 'Irrevocable Trust',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.REVOCABLE_TRUST,
          label: 'Revocable Trust',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.NON_PROFIT,
          label: 'Non-Profit',
        },
        {
          value: APPLICATION_BORROWER_BUSINESS_STRUCTURE.HOA,
          label: 'HOA',
        },
      ],
      required,
      requiredErrorMsg: 'Business Structure is required',
    },
    borrowerGrossRevenueLatest: {
      name: 'borrower_gross_revenue_latest' as const,
      label: 'Last Year Annual Gross Revenue',
      getLabel: (isForProfit: boolean) =>
        isForProfit
          ? 'Last Year Annual Gross Revenue'
          : 'Last Year Contributions and Support',
      required,
      requiredErrorMsg: 'Gross Business Revenue is required',
      tooltip:
        'What was the gross annual revenue of the business applying for credit in its last full fiscal year? Gross annual revenue is the amount of money the business earned before subtracting taxes and other expenses. You may provide gross annual revenue calculated using any reasonable method.',
    },
    borrowerBusinessEstablishedYear: {
      name: 'business_established_year' as const,
      label: 'Business Established Year',
      required,
      format: NUMBER_FORMATS.YEAR,
    },
    borrowerWasBusinessAcquired: {
      name: 'was_business_acquired' as const,
      getLabel: (businessName: string) =>
        `Was ${businessName} acquired from a previous owner?`,
      choices: YES_NO,
      required,
      requiredErrorMsg: 'This field is required',
    },
    borrowerYearsManagementInPlace: {
      name: 'years_management_in_place' as const,
      label: 'How many years has the present management been in place?',
      getTooltip: (borrowerLegalName: string) =>
        `Please provide the tenure of the present management of ${borrowerLegalName}.`,
      required: true,
      max: LIMITS.MAX_YEARS_ESTABLISHED,
    },
    borrowerManagementExperienceYears: {
      name: 'management_years_of_industry_experience' as const,
      getLabel: (industry: string) =>
        `How many years of experience related to ${industry} does the longest-tenured management member have?`,
      getTooltip: (borrowerLegalName) =>
        `Please enter the number of years that ${borrowerLegalName}'s longest-tenured management member has been working in ${borrowerLegalName}'s industry.`,
      max: 99,
      required,
    },
    borrowerLinesOfBusiness: {
      name: 'borrower_industry_lines_of_business',
      getLabel: (borrowerLegalName: string) =>
        `Describe ${borrowerLegalName}'s business`,
      getTooltip: (borrowerLegalName: string) =>
        `Briefly describe what line of business ${borrowerLegalName} is in and how ${borrowerLegalName} generates revenue.`,
      required,
      requiredErrorMsg: 'Primary business activity is required',
      maxLength: SF_MAX_CHAR_LIMITS.CHAR_FIELD,
    },
    businessOwnershipStatus: {
      name: 'business_ownership_status',
      label:
        'What is your business ownership status? (check one or more of the options below)',
      choices: BUSINESS_OWNERSHIP_STATUS_OPTIONS,
      exclusiveOptions: BUSINESS_OWNERSHIP_STATUS_EXCLUSIVE_OPTIONS,
      required,
      requiredErrorMsg: 'Select at least one option',
      tooltip:
        'Minority means Hispanic or Latino, American Indian or Alaska Native, Asian, Black or African American, or Native Hawaiian or Other Pacific Islander. A multi-racial or multi-ethnic individual is a minority for this purpose.',
    },
    numberOfEmployeesRange: {
      name: 'number_of_employees_range',
      label: 'Number of Employees Range',
      choices: NUMBER_OF_EMPLOYEES_RANGE_OPTIONS,
      required,
      requiredErrorMsg: 'This field is required',
    },
    principalTrustYearEstablished: {
      name: 'year_trust_was_established',
      label: 'Trust Established Year',
      tooltip:
        'Please provide the year when the fully executed final trust agreement was filed.',
      format: NUMBER_FORMATS.YEAR,
      required,
    },
    principalIsUSCitizen: {
      name: 'is_us_citizen' as const,
      getLabel: (
        principal_first_name: string,
        principal_last_name: string,
        index: number,
        isForProfit: boolean
      ) => {
        const defaultName = isForProfit
          ? `Principal ${index}`
          : `Authorized Board Member ${index}`;
        return principal_first_name && principal_last_name
          ? `Is ${principal_first_name} ${principal_last_name} a US citizen or permanent resident?`
          : `Is ${defaultName} a US citizen or permanent resident?`;
      },
      choices: YES_NO,
      requiredErrorMsg: 'Answer is required',
      required,
    },
    principalPercentOwned: {
      name: 'percent_ownership',
      getLabel: (businessName: string) => `Percentage Owned of ${businessName}`,
      required,
      requiredErrorMsg: 'Percentage Owned is required',
      tooltip:
        'If the principal does not have ownership, such as a non-profit, enter zero for ownership percentage.',
      max: 100,
    },
    propertyOwnerPercentOwned: {
      name: 'property_owner_percent_ownership',
      getLabel: (propertyOnwer: string) =>
        `Percentage Owned of ${propertyOnwer}`,
      required,
      requiredErrorMsg: 'Percentage Owned is required',
      max: 100,
    },
    propertyCompanyTenantMonthlyRentPayment: {
      name: 'prop_com_tenant_monthly_rent_payment',
      label: "Tenant's Monthly Rent",
      required,
    },
    applicantSameAsPrincipal: {
      name: 'applicant_same_as_principal' as const,
      getLabel: (firstName: string, lastName: string) =>
        firstName && lastName
          ? `Confirm you are ${firstName} ${lastName}`
          : `Confirm you are Principal`,
      choices: YES_NO,
      required,
      requiredErrorMsg: 'Choice is required',
    },
    principalRentOrOwn: {
      name: 'rent_or_own',
      getLabel: (firstName: string, lastName: string) =>
        firstName && lastName
          ? `Does ${firstName} ${lastName} rent or own their residence?`
          : `Does Principal rent or own their residence?`,
      required,
      requiredErrorMsg: 'Choice is required',
      choices: RENT_OWN,
    },
    principalRent: {
      name: 'monthly_rent',
      label: 'Monthly Rent',
      getRequired: (
        isPersonalGuarantor: boolean,
        rentOrOwn: RelatedParty['rent_or_own']
      ) => isPersonalGuarantor && rentOrOwn == 'Rent',
      required,
      requiredErrorMsg: 'Monthly rent is required (Enter 0 if none)',
      thousandSeparator: ',',
      decimalScale: 2,
      max: MAX_NUMBER_INPUT_VALUE,
    },
    principalMortgagePayment: {
      name: 'mortgage_payment',
      label: 'Monthly Mortgage Payment',
      tooltip: 'Combined monthly interest and principal payment.',
      getRequired: (
        isPersonalGuarantor: boolean,
        rentOrOwn: RelatedParty['rent_or_own']
      ) => isPersonalGuarantor && rentOrOwn == 'Own',
      required,
      requiredErrorMsg: 'Mortgage payment is required (Enter 0 if none)',
      thousandSeparator: ',',
      decimalScale: 2,
      max: MAX_NUMBER_INPUT_VALUE,
    },
    principalEthnicity: {
      name: 'ethnicity',
      label: 'What is your ethnicity? (check one or more)',
      choices: ETHNICITY_OPTIONS,
      required,
      requiredErrorMsg: 'Select at least one option',
      exclusiveOptions: ETHNICITY_OPTIONS_EXCLUSIVE,
    },
    principalSubEthnicity: {
      name: 'sub_ethnicity',
      label: 'Sub Ethnicity',
      choices: SUB_ETHNICITY_OPTIONS,
      required,
      requiredErrorMsg: 'Select at least one option',
    },
    principalOtherEthnicity: {
      name: 'other_ethnicity',
      label: 'Other Ethnicity',
      required,
      requiredErrorMsg: 'This field is required',
      tooltip:
        'Please specify your origin, for example, Argentinean, Colombian, Dominican, Nicaraguan, Salvadoran, Spaniard, and so on',
    },
    principalSexGender: {
      name: 'sex_gender',
      label: 'What is your sex/gender? (please specify)',
      required,
      requiredErrorMsg: 'This field is required',
      optOutText: 'I do not wish to provide my sex/gender',
    },
    principalRace: {
      name: 'race',
      label: 'What is your race? (check one or more)',
      choices: RACE_OPTIONS,
      exclusiveOptions: RACE_OPTIONS_EXCLUSIVE,
      required,
      requiredErrorMsg: 'Select at least one option',
    },
    principalSubRace: {
      name: 'sub_race',
      label: 'What is your sub-race? (check one or more)',
      choices: SUB_RACE_OPTIONS,
      required,
      requiredErrorMsg: 'Select at least one option',
      tooltip: OTHER_RACE_TOOLTIPS,
    },
    principalOtherRace: {
      name: 'other_race',
      label: 'Other Race',
      required,
      requiredErrorMsg: 'This field is required',
    },
    propertyCompanyUploadRentRollOrProvideTenantInfo: {
      name: 'system_power_rent_roll_or_tenant_info' as const,
      label: 'How would you like to provide tenant information?',
      choices: [
        {
          label: 'Upload rent roll',
          value: 'Upload rent roll',
        },
        {
          label: 'Enter information for a single tenant',
          value: 'Enter information for a single tenant',
        },
      ],
      required,
    },
    borrowerReligiousMembersServed: {
      name: 'members_served' as const,
      label: 'How many members does your religious organization serve?',
      tooltip:
        'Include active members only. Both in-person and remote memberships count.',
      required,
    },
    isOwnPropertyUnderConstruction: {
      name: 'is_own_property_under_construction' as const,
      getLabel: (address: string, borrowerLegalName: string) =>
        `Is this property ${
          address ? `at ${address} ` : ''
        }under construction or is ${borrowerLegalName} planning for major construction in the near future?`,
      choices: YES_NO,
      required,
    },
    willingToProvideCommercialGuarantor: {
      name: 'willing_to_provide_commercial_guarantor' as const,
      label: 'Are you willing to provide a commercial guarantor?',
      choices: YES_NO,
      required,
    },
    systemPropertyHasRelatedTenants: {
      name: 'system_property_has_related_tenants' as const,
      getLabel: (borrowerLegalName: string, propertyAddress: string) =>
        `Are any of the tenants of the project property ${
          propertyAddress ? `at ${propertyAddress} ` : ''
        }affiliated with ${borrowerLegalName}?`,
      choices: YES_NO,
      required,
    },
    propertyTenantWillingToBeCommercialGuarantor: {
      name: 'is_property_tenant_willing_to_be_cg' as const,
      label: `Is the related tenant willing to be a commercial guarantor?`,
      choices: YES_NO,
      required,
    },
    specialPaymentAcknowledgement: {
      name: 'special_payment_acknowledgment' as const,
      getLabel: (
        specialPaymentPercentage: number,
        specialPaymentAmount: string
      ) =>
        `I acknowledge the loan product I'm applying for consists of a ${specialPaymentPercentage}% special payment (${specialPaymentAmount}) due within 18 months after my loan funds. My interest rate and monthly payment will increase if I do not make the special payment.`,
      required,
      requiredErrorMsg: 'This field is required',
    },
    milestonePaymentAcknowledgement: {
      name: 'milestone_payment_acknowledgment' as const,
      getLabel: (
        interestPayerDuringConstruction:
          | 'Borrower'
          | 'Installer'
          | null
          | undefined
      ) =>
        `I acknowledge the loan product I am applying for includes Sunstone's Milestone Program. If approved, my loan may be disbursed upon the achievement of certain Milestones as my project is constructed, but before the project is completed. ${
          interestPayerDuringConstruction === 'Installer'
            ? 'My contractor has agreed to pay the interest only payments, but in the event my contractor defaults on the payments, '
            : ''
        }I will owe interest only payments at the Milestone Program interest rate.`,
      required,
      requiredErrorMsg: 'This field is required',
    },
    maturityPaymentAcknowledgement: {
      name: 'maturity_payment_acknowledgment' as const,
      getLabel: (formattedMaturityPaymentAmount: string) =>
        `I acknowledge that the loan product I am applying for has a lump-sum payment due at the end of the 10-year term. The lump sum payment is currently estimated at ${formattedMaturityPaymentAmount}`,
      required,
      requiredErrorMsg: 'This field is required',
    },
    commercialGuarantorLegalName: {
      name: 'commercial_guarantor_legal_name' as const,
      label: 'Enter the legal name of the commercial guarantor.',
      required,
      requiredErrorMsg: 'This field is required',
    },
    monetizeTaxCreditCheck: {
      name: 'monetize_tax_credit_check' as const,
      getLabel: (borrowerLegalName: string) =>
        `Will ${borrowerLegalName} monetize the tax credit through a traditional tax equity investment or a tax credit transfer?`,
      choices: YES_NO,
      required,
      requiredErrorMsg: 'This field is required',
    },
    beginInstallationExpected: {
      name: 'begin_installation_expected' as const,
      label: 'When do you want to begin construction of your project?',
      choices: BEGIN_INSTALLATION_EXPECTED_OPTIONS,
      required,
      requiredErrorMsg: 'This field is required',
    },
    primaryMotivationForSolar: {
      name: 'primary_motivation_for_solar' as const,
      label: 'What is your primary motivation for going solar?',
      choices: PRIMARY_MOTIVATION_FOR_SOLAR_OPTIONS,
      required,
      requiredErrorMsg: 'This field is required',
      allowedProjectTypes: ['solar', 'solar+'],
    },
    primaryMotivationForSolarOther: {
      name: 'primary_motivation_for_solar_other' as const,
      label: 'Please specify',
      required,
      requiredErrorMsg: 'This field is required',
    },
    projectType: {
      name: 'project_type' as const,
    },
    uploadOrListDebtObligations: {
      name: 'upload_or_list_debt_obligations' as const,
      getLabel: (borrowerLegalName: string) =>
        `Would you like to report all other outstanding debt obligations held by ${borrowerLegalName}
       via a debt schedule or list each one out manually?`,
      choices: [
        { label: 'Debt Schedule', value: 'Debt Schedule' },
        {
          label: 'List out debt obligations',
          value: 'List out debt obligations',
        },
      ],
      required,
      requiredErrorMsg: 'This field is required',
    },
    motivationAdditionalInformation: {
      name: 'motivation_additional_information' as const,
      label: 'Additional Information',
      required: false,
      requiredErrorMsg: 'This field is required',
    },
    companyDocuments: {
      ...loanFormModelOriginal.formField.companyDocuments,
      pgMortgageStatement: {
        name: 'Mortgage Statement' as const,
        getLabel: (address: string, required: boolean) =>
          `Please upload the most recent mortgage statement for the property at ${address}${
            required ? '' : ' (Optional)'
          }.`,
        tooltip:
          'The mortgage statement should be from the institution that issued your mortgage and contain the monthly payment, the payment due date, interest rate, detail how the payment is allocated between principal, interest, taxes, insurance and other obligations, and list the property address.',
        getRequired: (mortgageStatementRequired: boolean) =>
          mortgageStatementRequired === true,
        required,
        requiredErrorMsg: 'Mortgate Statement is required',
      },
      propertyCompanyRentRoll: {
        name: 'Property Company Rent Roll' as const,
        getLabel: (borrowerLegalName: string) =>
          `Rent Roll (including each of ${borrowerLegalName}'s tenants)`,
        tooltip:
          "Must include each tenant's name, move-in date, current lease term, renewal term, monthly rent payment, and confirmation that the tenant has made their payments in a satisfactory manner.",
        required,
      },
      nonProfitBylawsCopy: {
        name: 'Non-Profit Bylaws' as const,
        label: 'Organization Bylaws',
        tooltip:
          'Most recent bylaws for the organization. Bylaws should state which officer(s) are authorized to conduct banking business, and outline rules for major decisions, board meetings, voting, and other organizational rules.',
        required,
      },
      nonProfitBoardResolution: {
        name: 'Non-Profit Board Resolution' as const,
        label: 'Board Authorization to Borrow',
        tooltip:
          "Document stating the organization's board has authorized the loan. This must be a signed board resolution on official letterhead authorizing the financing of the project by the selected contractor and authorizing the signer(s) of the loan agreement(s).",
        required,
      },
      meetingMinutes: nwfMeetingMinutesFormField,
      religiousOrganizationAttendance: {
        name: 'Religious Organization Attendance' as const,
        label: 'Organization Attendance',
        tooltip:
          'Annual attendance over the last 3 years, including both in-person and remote attendees.',
        required,
      },
      religiousOrganizationDonations: {
        name: 'Religious Organization Donations' as const,
        label: 'Donations or Member Dues',
        tooltip:
          'Annual donations / tithes (“Giving Units”) by contributor (anonymized) over the last 3 years. ',
        required,
      },
      propertyAppraisal: {
        name: 'Property Appraisal' as const,
        label: 'Property Appraisal Report (Optional)',
        required: false,
      },
      taxReturnForLeasingPropCo: {
        name: 'Tax Return for Leasing PropCo' as const,
        getLabel: (propertyLegalNameOfOwner: string) =>
          `Upload most recent tax return or operating agreement for ${propertyLegalNameOfOwner}.`,
        getRequired: (taxReturnRequired: boolean) => taxReturnRequired === true,
        requiredErrorMsg: 'This document is required',
      },
    },
    hoaDocuments: {
      ...loanFormModelOriginal.formField.hoaDocuments,
      meetingMinutes: nwfMeetingMinutesFormField,
      priorYearBudgets: {
        name: 'Operating and Reserve Budgets' as const,
        tooltip: "Organization's budget from the previous year",
        label: `${currentYear - 1} Operating and Reserve Budgets`,
        required,
      },
      currentOccupantLedger: {
        name: 'Current Occupant Ledger' as const,
        label: 'Occupancy Ledger',
        getTooltip: (borrowerLegalName: string) =>
          `Register of all current homeowners, including purchase date, tenure, and if dues are current or past due.  For privacy, provide unit numbers only (do not provide names).`,
        required,
      },
      delinquencyReport: {
        name: 'Delinquency Report' as const,
        label: 'Delinquency Report',
        tooltip: 'Register of homeowner delinquency on assessments.',
      },
      reserveStudy: {
        name: 'Reserve Study' as const,
        label: 'Reserve Study',
        tooltip:
          'Reserve study containing reserve and capital expenditure projections. If not available, you can also submit a Capital Expenditure Budget.',
      },
    },
    solarSystemDocuments: {
      ...loanFormModelOriginal.formField.solarSystemDocuments,
      // Lease documents
      leaseOfTheSystemProperty: {
        name: 'Lease of the System Property' as const,
        getLabel: (propertyAddress: string) =>
          `Current lease for the project property ${
            propertyAddress ? `(${propertyAddress})` : ''
          }.`,
        tooltip:
          'The lease must be for the exact property where the project will be completed, whether a building, roof, or plot of land. If the lease is embedded within a Power Purchase Agreement, you may upload the Power Purchase Agreement.',
        required,
      },
    },
    loanObligationsDocuments: {
      debtSchedule: {
        name: 'Debt Schedule' as const,
        getLabel: (borrowerLegalName: string) =>
          `Upload a debt schedule for loans greater than $10,000 for ${borrowerLegalName}, and/or manually add each loan below.`,
        tooltip:
          "Please include the type or purpose of the loan, lender's name, current balance, monthly payment, and maturity year for each loan.",
        required,
        reviewLabel: 'Debt Schedule',
      },
    },
    borrowerIsHighRisk: {
      name: 'borrower_is_high_risk' as const,
      getLabel: (borrowerLegalName: string) =>
        `Does ${
          borrowerLegalName.trim() || 'the Business'
        } generate revenue from one or more of the following: 1) Cannabis (including CBD and hemp), 2) Currency exchange, including crypto currency, 3) Firearm and / or ammunition sales, 4) Gambling, including internet gambling, 5) Check-cashing services, or 6) High interest rate consumer loans (example: loans with > 30% APR)?`,
      required,
      choices: YES_NO,
      requiredErrorMsg: 'Answer is required',
    },
  },
};

const yearToDateFinancialStatementLabel = (borrowerLegalName: string) =>
  `${currentYear} Year-to-Date CPA or management prepared financial statements (P&L and Balance Sheet) for ${borrowerLegalName}`;

export const taxDocuments = {
  ...taxDocumentsOriginal,
  pgTaxes1YearAgo: {
    ...taxDocumentsOriginal.pgTaxes1YearAgo,
    getLabel: (
      _firstName: string | null | undefined,
      _lastName: string | null | undefined,
      index: number
    ) => `${getPG1Year()} individual IRS tax return for Principal ${index}.`,
  },
  financialStatementLastYearQ3: {
    name: 'Financial Statement Last Year Q3' as const,
    getLabel: (borrowerLegalName: string) =>
      `${lastYear} Financial Statements (P&L and balance sheet) for ${borrowerLegalName}, covering at a minimum ${lastYear} Q3 year-to-date.`,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
  },
  financialStatementQ1: {
    name: 'Financial Statement Q1' as const,
    getLabel: yearToDateFinancialStatementLabel,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
    required,
  },
  financialStatementQ2: {
    ...taxDocumentsOriginal.financialStatementQ2,
    getLabel: yearToDateFinancialStatementLabel,
  },
  financialStatementQ3: {
    ...taxDocumentsOriginal.financialStatementQ3,
    getLabel: yearToDateFinancialStatementLabel,
  },
  taxReturns4Year: {
    name: 'Tax Returns 4 Years Ago' as const,
    getLabel: (borrowerLegalName: string) =>
      `${fourYearsAgo} CPA-Prepared Financials OR Tax Returns for ${borrowerLegalName}.`,
    getTooltip: (borrowerLegalName: string) =>
      `Where possible, provide CPA-compiled/reviewed/audited financial statements, including an income statement and balance sheet. Otherwise, provide a copy of ${borrowerLegalName}'s tax return filed with the IRS for the ${fourYearsAgo} tax year.`,
  },
  financialStatementFullYear: {
    name: 'Financial Statement Full Year' as const,
    getLabel: (borrowerLegalName: string, isFbo = false) =>
      isFbo
        ? `${lastYear} CPA or management prepared financial statements`
        : `${lastYear} Full-Year Financial Statements (P&L and Balance Sheet) for ${borrowerLegalName}`,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
  },
  financialStatementTwoYearsAgoFullYear: {
    name: 'Financial Statement Two Years Ago Full Year' as const,
    getLabel: (borrowerLegalName: string, isFbo = false) =>
      isFbo
        ? `${twoYearsAgo} CPA or management prepared financial statements`
        : `${twoYearsAgo} Full-Year Financial Statements (P&L and Balance Sheet) for ${borrowerLegalName}`,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
  },
  financialStatementThreeYearsAgoFullYear: {
    name: 'Financial Statement Three Years Ago Full Year' as const,
    getLabel: (borrowerLegalName: string, isFbo = false) =>
      isFbo
        ? `${threeYearsAgo} CPA or management prepared financial statements`
        : `${threeYearsAgo} Full-Year Financial Statements (P&L and Balance Sheet) for ${borrowerLegalName}`,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
  },
  financialStatementFourYearsAgoFullYear: {
    name: 'Financial Statement Four Years Ago Full Year' as const,
    getLabel: (borrowerLegalName: string, isFbo = false) =>
      isFbo
        ? `${fourYearsAgo} CPA or management prepared financial statements`
        : `${fourYearsAgo} Full-Year Financial Statements (P&L and Balance Sheet) for ${borrowerLegalName}`,
    getTooltip: () =>
      'Financial statements should include an income statement and balance sheet.',
  },
};

// add (Optional) to field labels that are not required
Object.keys(loanFormModel.formField).forEach((key) => {
  if (
    !loanFormModel.formField[key]?.required &&
    !loanFormModel.formField[key]?.getRequired &&
    loanFormModel.formField[key]?.label &&
    !loanFormModel.formField[key]?.label.includes('Optional')
  ) {
    loanFormModel.formField[key].label =
      loanFormModel.formField[key].label + ' (Optional)';
  }
});

export default loanFormModel;

function extractNames(obj: any): string[] {
  let names: string[] = [];
  for (const key in obj) {
    if (obj[key]?.name) {
      names.push(obj[key].name);
    }
    if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      names = names.concat(extractNames(obj[key]));
    }
  }
  return names;
}

export const allFieldNames = [
  ...extractNames(loanFormModel),
  ...extractNames(taxDocuments),
];
